import { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Grid from '@/components/base/grid'
import Media from '@/components/base/media'
import Motion from '@/components/base/motion'
import ParallaxImage from '@/components/base/parallax-image'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { bannerRevealAnimation } from '@/components/sections/animations/banner'
import Spacer from '@/components/sections/spacer'

import { cn } from '@/lib/utils'
import { WPLink, WPMedia } from '@/types'

export type BannerProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    type:
      | '2_col_left'
      | '2_col_right'
      | 'small_image_left'
      | 'small_image_right'
    pretitle?: string
    title?: string
    text?: string
    link_1?: WPLink
    link_2?: WPLink
    media?: {
      media: WPMedia
    }
  }>

const Banner = ({
  type,
  pretitle,
  title,
  media,
  text,
  link_1,
  link_2
}: BannerProps) => {
  return (
    <Motion animation={bannerRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} data-layout="banner">
          <Spacer variant="xxl" />
          <Container>
            <Grid className="items-center gap-y-spacing-2xl">
              <div
                className={cn('col-span-2 h-full', {
                  'md:col-span-2 lg:col-span-3':
                    type === 'small_image_right' || type === 'small_image_left',
                  'md:col-start-5 lg:col-start-10': type === 'small_image_left',
                  'md:col-span-3 md:col-start-4 lg:col-span-6 lg:col-start-7':
                    type === '2_col_left',
                  'md:col-span-3 md:col-start-1 md:row-start-1 lg:col-span-6 lg:col-start-1 lg:row-start-1':
                    type === '2_col_right'
                })}
              >
                {media?.media?.poster?.url ? (
                  <div className="relative aspect-[4/3] h-auto w-full overflow-hidden rounded-radius-minimal md:aspect-auto md:h-full">
                    <ParallaxImage
                      data-motion-media
                      style={{
                        aspectRatio: `${media.media.poster.width} / ${media.media.poster.height}`
                      }}
                      className={'relative flex h-full w-full'}
                    >
                      <Media
                        media={media.media}
                        className="h-full w-full rounded-radius-minimal"
                        data-lightbox
                      />
                    </ParallaxImage>
                  </div>
                ) : null}
              </div>

              <div
                className={cn('col-span-2 grid gap-y-spacing-sm', {
                  'md:col-span-4 lg:col-span-8':
                    type === 'small_image_left' || type === 'small_image_right',
                  'lg:col-start-5': type === 'small_image_right',
                  'md:col-start-1 md:row-start-1 lg:col-start-1 lg:row-start-1':
                    type === 'small_image_left',
                  'md:col-span-3 md:col-start-1 md:row-start-1 lg:col-span-4 lg:col-start-1 lg:row-start-1':
                    type === '2_col_left',
                  'md:col-span-3 md:col-start-4 md:row-start-1 lg:col-span-4 lg:col-start-9 lg:row-start-1':
                    type === '2_col_right'
                })}
              >
                {pretitle ? (
                  <Typography
                    variant="small"
                    paragraph
                    className="condensed"
                    data-motion-pretitle
                  >
                    {pretitle}
                  </Typography>
                ) : null}

                {title ? (
                  <Typography variant="h5" component="h2" data-motion-title>
                    {title}
                  </Typography>
                ) : null}

                {text ? (
                  <Typography
                    variant="regular"
                    paragraph
                    className="text-text-primary-light dark:text-text-primary-dark"
                    data-motion-text
                  >
                    {text}
                  </Typography>
                ) : null}

                {link_1?.url || link_2?.url ? (
                  <div
                    className="mt-spacing-sm flex flex-col gap-spacing-2xs"
                    data-motion-cta
                  >
                    {link_1?.url ? (
                      <CtaButton
                        href={link_1.url}
                        target={link_1.target}
                        variant="primary"
                        className={cn('place-self-start', {
                          'w-full': link_1?.url && link_2?.url
                        })}
                      >
                        {link_1.title}
                      </CtaButton>
                    ) : null}
                    {link_2?.url ? (
                      <CtaButton
                        href={link_2.url}
                        target={link_2.target}
                        variant="secondary"
                        className={cn('place-self-start', {
                          'w-full': link_1?.url && link_2?.url
                        })}
                      >
                        {link_2.title}
                      </CtaButton>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </Grid>
          </Container>
          <Spacer variant="xxl" />
        </Section>
      )}
    </Motion>
  )
}

export default Banner
